import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AiPodCanvas } from './AiPodCanvas';
import { AiPodInspector, type AiPodAgentTypeOption } from './AiPodInspector';
import { autoArrangeAiPod } from '../../lib/ai-pod/autoArrangeAiPod';
import {
  AiPodCanvasState,
  AiPodCanvasNode,
  AiPodConnection,
  AiPodNodeTemplate,
} from '../../types/ai-pod-canvas';
import { Button } from '../ui/Button';

const GRID_SIZE = 24;
const snapToGrid = (position: { x: number; y: number }) => ({
  x: Math.round(position.x / GRID_SIZE) * GRID_SIZE,
  y: Math.round(position.y / GRID_SIZE) * GRID_SIZE,
});

/** Same set/order/icons/colors as WorkflowBuilder's WORKFLOW_BLOCK_TEMPLATES, so the
 * Pod canvas palette matches the tenant Workflow builder's. Trigger/Action here still
 * reference an AgentType (via the Inspector) rather than being agent-free. */
const POD_BLOCK_TEMPLATES: AiPodNodeTemplate[] = [
  {
    kind: 'trigger',
    name: 'Trigger',
    description: 'Start the pipeline from an inbound event or manual run',
    icon: '◉',
    color: '#16a34a',
    inputs: [],
    outputs: [{ id: 'out', name: 'start', type: 'object' }],
    defaultData: { nodeClass: 'trigger', triggerType: 'manual' },
  },
  {
    kind: 'router',
    name: 'Router',
    description: 'Branch the pipeline based on intent, outcome, or custom logic',
    icon: '◇',
    color: '#f59e0b',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [
      { id: 'true', name: 'true', type: 'object' },
      { id: 'false', name: 'false', type: 'object' },
    ],
    defaultData: { nodeClass: 'router', expression: '' },
  },
  {
    kind: 'action',
    name: 'Action',
    description: 'Run a system action or backend task inside the pipeline',
    icon: '□',
    color: '#2563eb',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [{ id: 'out', name: 'result', type: 'object' }],
    defaultData: { nodeClass: 'action' },
  },
  {
    kind: 'human-handoff',
    name: 'Human Handoff',
    description: 'Escalate to a human queue or support team',
    icon: '↗',
    color: '#ea580c',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [{ id: 'out', name: 'handoff', type: 'object' }],
    defaultData: { nodeClass: 'human-handoff' },
  },
  {
    kind: 'end',
    name: 'End',
    description: 'Terminate the pipeline with a success or failure outcome',
    icon: '◎',
    color: '#ef4444',
    inputs: [{ id: 'in', name: 'end', type: 'object' }],
    outputs: [],
    defaultData: { nodeClass: 'end', result: 'success' },
  },
];

function buildAgentTemplates(agentTypes: AiPodAgentTypeOption[]): AiPodNodeTemplate[] {
  return agentTypes.map((agentType) => ({
    kind: 'conversation',
    name: agentType.name,
    description: agentType.shortDescription || agentType.code,
    icon: '🤖',
    color: '#2563eb',
    inputs: [{ id: 'in', name: 'In', type: 'object' }],
    outputs: [{ id: 'out', name: 'Out', type: 'object' }],
    defaultData: { nodeClass: 'conversation', agentTypeId: agentType.id },
  }));
}

interface AiPodBuilderProps {
  agentTypes: AiPodAgentTypeOption[];
  initialNodes: AiPodCanvasNode[];
  initialConnections: AiPodConnection[];
  onSave: (payload: { nodes: AiPodCanvasNode[]; connections: AiPodConnection[] }) => Promise<void>;
}

export const AiPodBuilder: React.FC<AiPodBuilderProps> = ({
  agentTypes,
  initialNodes,
  initialConnections,
  onSave,
}) => {
  const [canvasState, setCanvasState] = useState<AiPodCanvasState>({
    nodes: initialNodes,
    connections: initialConnections,
    isConnecting: false,
  });
  const [saving, setSaving] = useState(false);
  const skipFirstDirtyRef = useRef(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (skipFirstDirtyRef.current) {
      skipFirstDirtyRef.current = false;
      return;
    }
    setHasUnsavedChanges(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasState.nodes, canvasState.connections]);

  const agentTemplates = useMemo(() => buildAgentTemplates(agentTypes), [agentTypes]);

  const handleUpdateCanvasState = useCallback((updates: Partial<AiPodCanvasState>) => {
    setCanvasState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleAddNode = useCallback((template: AiPodNodeTemplate, position?: { x: number; y: number }) => {
    setCanvasState((prev) => {
      const fallbackPosition = {
        x: 72 + (prev.nodes.length % 3) * 264,
        y: 72 + Math.floor(prev.nodes.length / 3) * 220,
      };
      const node: AiPodCanvasNode = {
        id: `node-${Date.now()}`,
        kind: template.kind,
        name: template.name,
        title: template.name,
        nodeClass: template.kind,
        position: snapToGrid(position ?? fallbackPosition),
        data: { ...template.defaultData },
        inputs: template.inputs,
        outputs: template.outputs,
      };
      return { ...prev, nodes: [...prev.nodes, node] };
    });
  }, []);

  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<AiPodCanvasNode>) => {
    setCanvasState((prev) => {
      const nodes = prev.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const data = updates.data ? { ...node.data, ...updates.data } : node.data;
        return { ...node, ...updates, data };
      });
      const updatedNode = nodes.find((n) => n.id === nodeId);
      const validInputIds = new Set((updatedNode?.inputs ?? []).map((i) => i.id));
      const validOutputIds = new Set((updatedNode?.outputs ?? []).map((o) => o.id));
      const connections = prev.connections.filter((c) => {
        if (c.targetNodeId === nodeId && !validInputIds.has(c.targetInputId)) return false;
        if (c.sourceNodeId === nodeId && !validOutputIds.has(c.sourceOutputId)) return false;
        return true;
      });
      return { ...prev, nodes, connections };
    });
  }, []);

  const handleDeleteNode = useCallback((nodeId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.filter((n) => n.id !== nodeId),
      connections: prev.connections.filter((c) => c.sourceNodeId !== nodeId && c.targetNodeId !== nodeId),
      selectedNodeId: prev.selectedNodeId === nodeId ? undefined : prev.selectedNodeId,
    }));
  }, []);

  const handleAddConnection = useCallback((connection: AiPodConnection) => {
    setCanvasState((prev) => ({ ...prev, connections: [...prev.connections, connection] }));
  }, []);

  const handleDeleteConnection = useCallback((connectionId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      connections: prev.connections.filter((c) => c.id !== connectionId),
      selectedConnectionId: prev.selectedConnectionId === connectionId ? undefined : prev.selectedConnectionId,
    }));
  }, []);

  const handleUpdateConnection = useCallback((connectionId: string, updates: Partial<AiPodConnection>) => {
    setCanvasState((prev) => ({
      ...prev,
      connections: prev.connections.map((c) => (c.id === connectionId ? { ...c, ...updates } : c)),
    }));
  }, []);

  const handleAddRouterBranch = useCallback((nodeId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const n = node.outputs.length + 1;
        return { ...node, outputs: [...node.outputs, { id: `branch-${Date.now()}-${n}`, name: `Branch ${n}`, type: 'object' }] };
      }),
    }));
  }, []);

  const handleAutoArrange = useCallback(() => {
    setCanvasState((prev) => ({ ...prev, nodes: autoArrangeAiPod(prev.nodes, prev.connections) }));
  }, []);

  const onDragStartTemplate = useCallback((template: AiPodNodeTemplate) => (e: React.DragEvent) => {
    const json = JSON.stringify(template);
    e.dataTransfer.setData('application/json', json);
    e.dataTransfer.setData('text/plain', json);
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const selectedNode = canvasState.nodes.find((n) => n.id === canvasState.selectedNodeId);
  const selectedConnection = canvasState.connections.find((c) => c.id === canvasState.selectedConnectionId);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave({ nodes: canvasState.nodes, connections: canvasState.connections });
      setHasUnsavedChanges(false);
      toast.success('AI Pod configuration saved');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to save AI Pod configuration');
    } finally {
      setSaving(false);
    }
  }, [canvasState.nodes, canvasState.connections, onSave]);

  return (
    <div className="flex h-full w-full">
      {/* Palette */}
      <aside className="w-64 shrink-0 overflow-y-auto border-r border-gray-100 bg-white p-4">
        <div className="mb-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Workflow Blocks</h3>
          <div className="space-y-2">
            {POD_BLOCK_TEMPLATES.map((template) => (
              <div
                key={template.kind}
                draggable
                onDragStart={onDragStartTemplate(template)}
                className="cursor-grab rounded-lg border border-gray-200 p-2 text-sm hover:border-blue-300 hover:bg-blue-50/50"
                style={{ borderLeftColor: template.color, borderLeftWidth: 3 }}
              >
                <div className="flex items-center gap-2">
                  <span>{template.icon}</span>
                  <span className="font-medium text-gray-800">{template.name}</span>
                </div>
                <p className="mt-0.5 text-xs text-gray-500">{template.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Agent Types</h3>
          <div className="space-y-2">
            {agentTemplates.length === 0 ? (
              <p className="text-xs text-gray-400">No active agent types found.</p>
            ) : (
              agentTemplates.map((template, idx) => (
                <div
                  key={agentTypes[idx]?.id ?? idx}
                  draggable
                  onDragStart={onDragStartTemplate(template)}
                  className="cursor-grab rounded-lg border border-gray-200 p-2 text-sm hover:border-blue-300 hover:bg-blue-50/50"
                  style={{ borderLeftColor: template.color, borderLeftWidth: 3 }}
                >
                  <div className="flex items-center gap-2">
                    <span>{template.icon}</span>
                    <span className="font-medium text-gray-800 truncate">{template.name}</span>
                  </div>
                  {template.description ? <p className="mt-0.5 truncate text-xs text-gray-500">{template.description}</p> : null}
                </div>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Configure Pipeline</h2>
            <p className="text-xs text-gray-500">Drag blocks/agent types onto the canvas, then connect their ports to build the pipeline.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button tone="outline-subtle"
              type="button"
              className="rounded-md border px-3 py-2 text-sm"
              onClick={handleAutoArrange}
            >
              Auto Arrange
            </Button>
            <Button tone="blue"
              type="button"
              className="rounded-md px-4 py-2 text-sm disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : hasUnsavedChanges ? 'Save Changes*' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="relative flex-1">
          <AiPodCanvas
            canvasState={canvasState}
            onUpdateCanvasState={handleUpdateCanvasState}
            onUpdateNode={handleUpdateNode}
            onDeleteNode={handleDeleteNode}
            onAddConnection={handleAddConnection}
            onDeleteConnection={handleDeleteConnection}
            onAddNode={handleAddNode}
            onAddRouterBranch={handleAddRouterBranch}
          />
        </div>
      </div>

      <AiPodInspector
        selectedNode={selectedNode}
        selectedConnection={selectedConnection}
        agentTypes={agentTypes}
        onUpdateNode={handleUpdateNode}
        onDeleteNode={handleDeleteNode}
        onUpdateConnection={handleUpdateConnection}
        onDeleteConnection={handleDeleteConnection}
        onAddRouterBranch={handleAddRouterBranch}
        onClose={() => handleUpdateCanvasState({ selectedNodeId: undefined, selectedConnectionId: undefined })}
        onSave={handleSave}
      />
    </div>
  );
};

export default AiPodBuilder;
